import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaBolt,
  FaEnvelope,
  FaLock,
  FaUser,
  FaPhone,
  FaArrowRight,
  FaArrowLeft,
  FaGoogle,
  FaApple,
  FaSpinner,
  FaKey,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import Header from "./header";
import Footer from "./Footer";
import "./auth.css";
import { Helmet } from "react-helmet-async";

// --- CONFIGURATION ---
const AUTH_API_URL = `${process.env.REACT_APP_API_URL}`;
const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ MOVE THESE HERE
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [cities, setCities] = useState([]);

  const [showPassword, setShowPassword] = useState(false);
  const [RegNewPassword, setRegNewPassword] = useState(false);
  const [FGNewPassword, setFGNewConPassword] = useState(false);
  const [FGConPassword, setFGConConPassword] = useState(false);

  const [view, setView] = useState(location.state?.view || "login");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [tempUserId, setTempUserId] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [countryCode, setCountryCode] = useState("+91"); // default

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    mobile: "",
    confirmPassword: "",
    input: "",
  });

  const API_BASE = `${process.env.REACT_APP_API_URL}`;

  const apiClient = axios.create({
    timeout: 10000,
    headers: { "Content-Type": "application/json" },
  });

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await apiClient.get(`${API_BASE}/states`);
        setStates(res.data.result || []);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const res = await apiClient.get(`${API_BASE}/country_code`);
        const countries = res.data.result || [];
        setCountryList(countries);

        const india = countries.find((c) => c.iso === "IN");
        if (india) setCountryCode("+" + india.phonecode);
      } catch (err) {
        console.error("Error fetching country codes:", err);
      }
    };

    fetchCountryCodes();
  }, []);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);

  // --- HELPERS ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
    setSuccessMsg("");
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault(); // ✅ Prevent page refresh

    setError("");
    setSuccessMsg("");

    if (step === 1) {
      await handleSendOtp();
    } else if (step === 2) {
      await handleVerifyOtp();
    } else if (step === 3) {
      await handleRegister();
    }
  };

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const getOtpString = () => otp.join("");

  const sanitizeMobile = (inputValue) => {
    if (!inputValue) return "";
    let clean = inputValue.replace(/\D/g, "");
    if (clean.length === 12 && clean.startsWith("91")) {
      clean = clean.slice(2);
    }
    return clean;
  };

  // --- AUTH COMPLETION HANDLER ---
  const completeAuthentication = async (userData) => {
    try {
      sessionStorage.setItem("loginTime", Date.now());
      // 1. Save User Data & ID
      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem(
        "user_identifier",
        userData.email || userData.mobile,
      );
      // Ensure user_id is stored separately if present
      if (userData.id || userData.user_id) {
        localStorage.setItem("user_id", userData.id || userData.user_id);
      }

      // 2. Trigger Events
      window.dispatchEvent(new Event("auth-change"));

      // 3. Sync Cart (Background)
      const localCart = JSON.parse(localStorage.getItem("cart")) || [];
      if (localCart.length > 0) {
        await Promise.all(
          localCart.map((item) =>
            axios.post(`${AUTH_API_URL}/add-to-cart`, {
              user_identifier: userData.email || userData.mobile,
              product_id: item.productid || item.id,
              quantity: item.quantity || 1,
              product_name: item.productname || item.name,
              price: item.price,
            }),
          ),
        );
        localStorage.removeItem("cart");
        window.dispatchEvent(new Event("cart-change"));
      }

      let targetPath =
        view === "register" ? "/profile" : location.state?.from || "/";

      const { data } = await axios.get(
        `${AUTH_API_URL}/count-list?user_identifier=${userData.email}`,
      );

      if (!location.state?.from) {
        targetPath = Number(data.cart_count) > 0 ? "/cart" : "/";
      } else {
        targetPath = location.state.from;
      }
      navigate(targetPath, { replace: true });
    } catch (e) {
      console.error("Auth completion error", e);
    }
  };

  // --- API ACTIONS ---

  // 1. SEND OTP
  const handleSendOtp = async () => {
    if (!formData.name || !formData.mobile)
      return setError("Please enter Name and Mobile Number.");
    setLoading(true);
    setError("");

    try {
      const cleanMobile = sanitizeMobile(formData.mobile);
      const payload = {
        name: formData.name,
        mobile: cleanMobile,
        countrycode: countryCode,
      };

      const response = await axios.post(`${AUTH_API_URL}/send-otp`, payload);

      if (response.data.status === true) {
        setSuccessMsg("OTP sent successfully!");
        // Capture user_id if sent here (sometimes sent in step 1)
        if (response.data.user_id) setTempUserId(response.data.user_id);
        setStep(2);
      } else {
        setError(response.data.message || "Failed to send OTP.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Error sending OTP",
      );
    } finally {
      setLoading(false);
    }
  };

  // 2. VERIFY OTP
  const handleVerifyOtp = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        mobile: sanitizeMobile(formData.mobile),
        otp: getOtpString(),
      };
      const response = await axios.post(`${AUTH_API_URL}/verify-otp`, payload);

      if (response.data.status === true) {
        setSuccessMsg("Mobile Verified!");
        setStep(3);
      } else {
        setError(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };
  // 3. REGISTER
  const handleRegister = async () => {
    if (!formData.email || !formData.password || !selectedState)
      return setError("Fill all fields");

    // ✅ Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return setError("Please enter a valid email address");
    }

    if (!tempUserId) return setError("User ID missing. Please retry OTP step.");

    setLoading(true);
    setError("");

    try {
      const payload = {
        password: formData.password,
        email: formData.email,
        user_id: String(tempUserId),
        state: selectedState,
      };

      const response = await axios.post(`${AUTH_API_URL}/register`, payload);

      // Check for success
      if (
        response.data.message === "User registered successfully" ||
        response.data.user_id
      ) {
        setSuccessMsg("User registered successfully");
        const userObj = {
          id: response.data.user_id || tempUserId,
          email: formData.email,
          name: formData.name,
          mobile: formData.mobile,
          state: selectedState,
          token: response.data.token || "", // optional if API provides
        };

        await completeAuthentication(userObj); // ✅ Automatically login and redirect
      } else {
        setError(response.data.message || "Registration failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Registration Error");
    } finally {
      setLoading(false);
    }
  };

  // 4. LOGIN
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = { email: formData.email, password: formData.password };
      const response = await axios.post(`${AUTH_API_URL}/login`, payload);

      if (
        response.data.status === true ||
        response.data.message === "Login successful"
      ) {
        const apiUser = response.data.user || {};

        // Derive name if missing
        let finalName = apiUser.name || response.data.name;
        if (!finalName && formData.email) {
          const extracted = formData.email.split("@")[0];
          finalName = extracted.charAt(0).toUpperCase() + extracted.slice(1);
        }

        const userObj = {
          id: response.data.id, // ✅ CORRECT
          email: formData.email,
          token: response.data.token,
          name: finalName || formData.email.split("@")[0],
          address: response.data.address,
          mobile: response.data.mobile,
          state: response.data.state,
        };
        await completeAuthentication(userObj);
      } else {
        setError(response.data.message || "Invalid credentials");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // 5. FORGOT PASSWORD
  const handleForgotPassword = async () => {
    if (formData.password !== formData.confirmPassword)
      return setError("Passwords do not match");
    setLoading(true);

    try {
      const payload = {
        input: formData.input.trim(),
        newPassword: formData.password,
        confirmPassword: formData.confirmPassword,
      };
      const response = await axios.post(
        `${AUTH_API_URL}/forget_password`,
        payload,
      );

      if (response.data.status === true) {
        alert("Password reset successfully! Please login.");
        setView("login");
        setStep(1);
      } else {
        setError(response.data.message || "Reset failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER ---
  return (
    <div className="page-layout">
      <Helmet>
        {/* Primary Meta */}
        <title>Login / Register | CGRMART</title>

        <meta
          name="description"
          content="Login or create your CGRMART account to manage EV charger purchases, track orders, and access smart charging solutions across India."
        />
        
        <meta
          name="keywords"
          content="CGRMART login, EV charger account, electric vehicle charging India, Tucker EV login, EV products account"
        />

        <meta name="robots" content="index, follow" />

        {/* Canonical */}
        <link rel="canonical" href="https://cgrmart.com/auth" />

        {/* Open Graph (Facebook, LinkedIn) */}
        <meta property="og:title" content="Login | CGRMART EV Charging" />
        <meta
          property="og:description"
          content="Access your CGRMART account to manage EV chargers, orders, and smart charging services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cgrmart.com/auth" />
        <meta property="og:image" content="https://cgrmart.com/logo.png" />

        {/* Twitter */}
        <meta name="twitter:title" content="Login | CGRMART" />
        <meta
          name="twitter:description"
          content="Sign in to CGRMART to manage EV charging products and services."
        />
        <meta name="twitter:image" content="https://cgrmart.com/logo.png" />

        {/* Extra SEO (Optional but Powerful) */}
        <meta name="author" content="CGRMART" />
        <meta name="theme-color" content="#ffffff" />

        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />
      </Helmet>

      <div className="auth-container">
        <div className="login-wrapper">
          <div className="visual-panel">
            <div className="visual-overlay"></div>
            <div className="visual-content">
              <h2 className="revolution-title">Join the Revolution.</h2>
              <p className="revolution-subtitle">
                Create an account to unlock exclusive pricing and manage your
                lifestyle.
              </p>
            </div>
          </div>

          <div className="form-panel">
            <div className="brand">
              <img
                src="/logo.png"
                alt="CGRMART Logo"
                style={{
                  width: "50%",
                  height: "90px",
                  marginLeft: "25%",
                }}
              />
            </div>

            {view !== "forgot" && (
              <div className="main-toggle">
                <div
                  className={`mt-btn ${view === "login" ? "active" : ""}`}
                  onClick={() => {
                    setView("login");
                    setStep(1);
                    setError("");
                    setSuccessMsg("");

                    // ✅ ADD THIS RESET
                    setFormData({
                      email: "",
                      password: "",
                      name: "",
                      mobile: "",
                      confirmPassword: "",
                      input: "",
                    });

                    setOtp(["", "", "", "", "", ""]);
                    setTempUserId(null);
                  }}
                >
                  Log In
                </div>
                <div
                  className={`mt-btn ${view === "register" ? "active" : ""}`}
                  onClick={() => {
                    setView("register");
                    setStep(1);
                    setError("");
                    setSuccessMsg("");

                    // ✅ ADD THIS RESET
                    setFormData({
                      email: "",
                      password: "",
                      name: "",
                      mobile: "",
                      confirmPassword: "",
                      input: "",
                    });

                    setOtp(["", "", "", "", "", ""]);
                    setTempUserId(null);
                  }}
                >
                  Create Account
                </div>
              </div>
            )}

            {error && <div className="error-msg">{error}</div>}
            {successMsg && <div className="success-msg">{successMsg}</div>}

            {/* --- LOGIN --- */}
            {view === "login" && (
              <div className="fade-in">
                <div className="header-text">
                  <h1>Welcome back</h1>
                  <p>Enter your credentials to access your account.</p>
                </div>
                <form onSubmit={handleLogin} autoComplete="off">
                  <div className="input-group">
                    <label className="input-label">Email Address</label>
                    <div className="input-wrapper">
                      <FaEnvelope className="input-icon" />
                      <input
                        type="text"
                        name="email"
                        className="form-input"
                        placeholder="name@example.com"
                        maxLength={100}
                        autoComplete="off"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="input-group">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        className="input-label"
                        style={{ marginBottom: 0 }}
                      >
                        Password
                      </label>
                      <span
                        onClick={() => {
                          setView("forgot");
                          setStep(1);
                          setError("");
                        }}
                        className="forgot-password-link"
                      >
                        Forgot Password
                      </span>
                    </div>

                    <div
                      className="input-wrapper"
                      style={{ position: "relative" }}
                    >
                      <FaLock className="input-icon" />

                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="form-input"
                        placeholder="••••••••"
                        value={formData.password}
                        maxLength={20}
                        autoComplete="off"
                        onChange={handleInputChange}
                        required
                      />

                      {/* 👁 Eye Icon */}
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#888",
                        }}
                      >
                        {showPassword ? <FaEye /> : <FaEyeSlash />}
                      </span>
                    </div>
                  </div>

                  <button
                    className="btn-primary"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <FaSpinner className="fa-spin" />
                    ) : (
                      <>
                        Sign In <FaArrowRight />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* --- REGISTER --- */}
            {view === "register" && (
              <div className="fade-in">
                <form autoComplete="off" onSubmit={handleRegisterSubmit}>
                  <div className="register-slider">
                    <div
                      className={`step-group ${
                        step === 1 ? "active" : step > 1 ? "prev" : ""
                      }`}
                    >
                      <div className="header-text">
                        <h1>Who you are?</h1>
                        <p>We'll send a code to verify your mobile number.</p>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Full Name</label>
                        <div className="input-wrapper">
                          <FaUser className="input-icon" />
                          <input
                            type="text"
                            name="name"
                            className="form-input"
                            placeholder="John Doe"
                            maxLength={100}
                            autoComplete="off"
                            value={formData.name}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="input-group">
                        <label className="input-label">Mobile Number</label>
                        <div
                          className="input-wrapper"
                          style={{ display: "flex", gap: "8px" }}
                        >
                          <select
                            value={countryCode}
                            class="form-input"
                            onChange={(e) => setCountryCode(e.target.value)}
                            style={{
                              width: "100px",
                            }}
                          >
                            {countryList.map((country) => (
                              <option
                                key={country.id}
                                value={"+" + country.phonecode}
                              >
                                +{country.phonecode} {country.iso} -{" "}
                                {country.nicename}
                              </option>
                            ))}
                          </select>

                          <input
                            type="tel"
                            name="mobile"
                            className="form-input"
                            placeholder="9876543210"
                            maxLength={10}
                            minLength={10}
                            value={formData.mobile}
                            autoComplete="off"
                            onChange={(e) => {
                              let v = e.target.value.replace(/\D/g, "");
                              if (v && !/^[6-9]/.test(v)) return;
                              setFormData({ ...formData, mobile: v });
                            }}
                            style={{ flex: 1, padding: "16px 12px" }}
                          />
                        </div>
                      </div>
                      <button
                        className="btn-primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <FaSpinner className="fa-spin" />
                        ) : (
                          "Send OTP"
                        )}{" "}
                        <FaArrowRight />
                      </button>
                    </div>

                    <div
                      className={`step-group ${
                        step === 2 ? "active" : step > 2 ? "prev" : ""
                      }`}
                    >
                      <div className="header-text">
                        <h1>Verify Number</h1>
                        <p>Enter the code sent to {formData.mobile}.</p>
                      </div>
                      <div className="otp-container">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => (otpRefs.current[index] = el)}
                            type="text"
                            className="otp-digit"
                            autoComplete="off"
                            maxLength={1}
                            value={digit}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          />
                        ))}
                      </div>
                      <button
                        className="btn-primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <FaSpinner className="fa-spin" />
                        ) : (
                          "Verify OTP"
                        )}
                      </button>
                      <button
                        className="btn-back"
                        onClick={() => setStep(1)}
                        type="button"
                      >
                        <FaArrowLeft /> Change Number
                      </button>
                    </div>

                    <div className={`step-group ${step === 3 ? "active" : ""}`}>
                      <div className="header-text">
                        <h1>Secure Account</h1>
                        <p>Set up your login credentials.</p>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Email Address</label>
                        <div className="input-wrapper">
                          <FaEnvelope className="input-icon" />
                          <input
                            type="email"
                            name="email"
                            className="form-input"
                            placeholder="name@example.com"
                            maxLength={100}
                            autoComplete="off"
                            value={formData.email}
                            onChange={handleInputChange}
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleForgotPassword();
                            }}
                          />
                        </div>
                      </div>

                      <div className="input-group">
                        <label className="input-label">State</label>

                        <select
                          value={selectedState}
                          className="form-input"
                          onChange={(e) => {
                            setSelectedState(e.target.value);
                            setSelectedDistrict("");
                            setCities([]);
                          }}
                        >
                          <option value="">Select State</option>

                          {states.map((state, index) => (
                            <option
                              key={state.state_id || index}
                              value={state.state_id}
                            >
                              {state.state_title}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="input-label">
                          Create Password
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#64748B",
                              marginLeft: "6px",
                              fontWeight: "400",
                            }}
                          >
                            (Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1
                            special character)
                          </span>
                        </label>

                        <div className="input-wrapper">
                          <FaLock className="input-icon left" />

                          <input
                            type={RegNewPassword ? "text" : "password"}
                            name="password"
                            className="form-input"
                            placeholder="Example: Abc@1234"
                            value={formData.password}
                            onChange={handleInputChange}
                          />

                          <button
                            type="button"
                            className="eye-toggle"
                            onClick={() => setRegNewPassword((prev) => !prev)}
                          >
                            {RegNewPassword ? <FaEye /> : <FaEyeSlash />}
                          </button>
                        </div>
                      </div>

                      <button
                        className="btn-primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <FaSpinner className="fa-spin" />
                        ) : (
                          "Create Account"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* --- FORGOT PASSWORD --- */}
            {view === "forgot" && (
              <div className="fade-in">
                <form autoComplete="off">
                  <div className="register-slider">
                    <div
                      className={`step-group ${
                        step === 1 ? "active" : step > 1 ? "prev" : ""
                      }`}
                    >
                      <div className="header-text">
                        <h1>Reset Password</h1>
                        <p>Enter your registered email.</p>
                      </div>
                      <div className="input-group">
                        <label className="input-label">Email</label>
                        <div className="input-wrapper">
                          <FaUser className="input-icon" />
                          <input
                            type="text"
                            name="input"
                            className="form-input"
                            placeholder="Enter Email"
                            value={formData.input}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="input-group">
                        <label className="input-label">
                          New Password
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#64748B",
                              marginLeft: "6px",
                              fontWeight: "400",
                            }}
                          >
                            (Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1
                            special character)
                          </span>
                        </label>
                        <div className="input-wrapper">
                          <FaLock className="input-icon" />

                          <input
                            type={FGNewPassword ? "text" : "password"}
                            name="password"
                            className="form-input"
                            maxLength={20}
                            autoComplete="off"
                            placeholder="Enter New Password"
                            value={formData.password}
                            onChange={handleInputChange}
                          />

                          <span
                            className="password-toggle"
                            onClick={() => setFGNewConPassword(!FGNewPassword)}
                          >
                            {FGNewPassword ? <FaEye /> : <FaEyeSlash />}
                          </span>
                        </div>
                      </div>

                      <div className="input-group">
                        <label className="input-label">
                          Confirm Password{" "}
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#64748B",
                              marginLeft: "6px",
                              fontWeight: "400",
                            }}
                          >
                            (Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1
                            special character)
                          </span>
                        </label>
                        <div className="input-wrapper">
                          <FaKey className="input-icon" />

                          <input
                            type={FGConPassword ? "text" : "password"}
                            name="confirmPassword"
                            className="form-input"
                            maxLength={20}
                            autoComplete="off"
                            placeholder="Enter Confirm Password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                          />

                          <span
                            className="password-toggle"
                            onClick={() => setFGConConPassword(!FGConPassword)}
                          >
                            {FGConPassword ? <FaEye /> : <FaEyeSlash />}
                          </span>
                        </div>
                      </div>

                      <button
                        className="btn-primary"
                        type="submit"
                        onClick={handleForgotPassword}
                        disabled={loading}
                      >
                        {loading ? (
                          <FaSpinner className="fa-spin" />
                        ) : (
                          "Reset Password"
                        )}
                      </button>
                      <button
                        className="btn-back"
                        type="button"
                        onClick={() => setView("login")}
                      >
                        <FaArrowLeft /> Back to Login
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AuthPage;
